import {Component, EventEmitter, HostListener, Inject, Input, OnInit, Output} from '@angular/core';
import {DYNAMIC_FORMS_SERVICE, DynamicFormsService} from '../DynamicFormsService';
import {BusinessObjectDetails} from '../../model/BusinessObjectDetails';
import {DynamicFormControl} from '../DynamicFormControl';
import {BoInstanceToSave} from '../../model/BoInstanceToSave';
import {BoAutocompleteOptionsRequest} from '../../model/BoAutocompleteOptionsRequest';
import {BusinessFieldRef} from '../../model/BusinessFieldRef';
import {BoAutocompleteOption} from '../../model/BoAutocompleteOption';
import {BoInstance} from '../../model/BoInstance';
import {BoInstanceSelectorRecord} from '../../model/BoInstanceSelectorRecord';
import {BoInstanceAmountToSave} from '../../model/BoInstanceAmountToSave';
import {BoFieldWithValue} from '../../model/BoFieldWithValue';
import {debounceTime, distinctUntilChanged, filter, switchMap} from 'rxjs/operators';
import {DEBOUNCE_TIME, safeObserve} from '../Util';
import {SubSink} from '../../lib/SubSink';

@Component({
  selector: 'app-create-boi-overlay',
  templateUrl: './create-boi-overlay.component.html',
  styleUrls: ['./create-boi-overlay.component.scss']
})
export class CreateBoiOverlayComponent implements OnInit {

  @Input() headers: BusinessFieldRef[];
  @Input() businessObjectId: string;
  @Input() boDetails: BusinessObjectDetails;
  @Input() costCalcEnabled: boolean;
  @Input() costCalcFieldId: string;
  @Input() ownerBoId: string;
  @Input() ownerFieldId: string;
  @Input() ownerBoInstanceId: string;
  @Input() boiDraftId: string;
  @Output() boInstanceAdded = new EventEmitter<BoInstanceSelectorRecord>();
  @Output() boiAmountChanged = new EventEmitter<BoInstanceAmountToSave>();
  @Output() boInstanceCreated = new EventEmitter<BoInstanceToSave>();
  @Output() closeClicked = new EventEmitter<void>();
  @Output() saveClicked = new EventEmitter<void>();

  completedBoInstance: BoInstanceSelectorRecord;
  formControl: DynamicFormControl;
  boInstanceAmount = 1;
  private readonly formSubs = new SubSink();
  private boiToSave: BoInstanceToSave;

  constructor(@Inject(DYNAMIC_FORMS_SERVICE)
              private readonly service: DynamicFormsService) { }

  async ngOnInit() {

    this.prepareFields();

    this.boiToSave = await this.createBoiToSave();

    this.formControl = new DynamicFormControl(this.createFormControlConfig());

    for (const field of this.boDetails.fields) {

      this.formSubs.sink = this.formControl.valueChanged.pipe(
        filter(({fieldId}) => fieldId === field.fieldId),
        debounceTime(DEBOUNCE_TIME),
        distinctUntilChanged()
      ).subscribe(({fieldId, value}) => this.fillAutocompleteOptions(fieldId, value));

    }

    this.formSubs.sink = this.formControl.autocompleteClicked
      .pipe(
        switchMap(option => this.loadBoInstance(option)),
        filter(x => !!x),
      ).subscribe(boi => this.completeForm(boi));

  }

  private createFormControlConfig() {
    return {
      fields: this.boDetails.fields,
      needAutocomplete: true,
      ownerData: {
        ownerBoId: this.businessObjectId,
        ownerBoInstanceId: undefined,
      },
      boiDraftId: this.boiDraftId,
    };
  }

  private async createBoiToSave() {
    return {
      businessId: await this.service.loadNewId(),
      businessObjectId: this.boDetails.id,
      returnRecordFieldIds: this.headers.map(x => x.fieldId),
      fields: [],
      draftId: this.boiDraftId,
      isForCreate: true
    };
  }

  private prepareFields() {
    const toShow = this.headers.filter(x => x.toShow).map(x => x.fieldId);
    this.boDetails.fields = this.boDetails.fields
      .filter(x => x.isRequired || x.isUnique || toShow.includes(x.fieldId));
  }

  @HostListener('keydown.control.enter')
  onCtrlEnter() {
    this.saveBoInstance();
  }

  private async fillAutocompleteOptions(fieldId: string, value: string) {
    if (!value) {
      this.formControl.closeAutocompleteFor(fieldId);
      return;
    }
    const req: BoAutocompleteOptionsRequest = this.createReq(fieldId, value);

    const options = await this.service.loadBoAutocompleteOptions(req);
    this.formControl.showAutocompleteFor(fieldId, options);
  }

  private createReq(fieldId: string, value: string): BoAutocompleteOptionsRequest {
    return {
      fieldId,
      startWith: value,
      businessObjectId: this.businessObjectId,
      ownerBoId: this.ownerBoId,
      ownerFieldId: this.ownerFieldId,
      ownerBoInstanceId: this.ownerBoInstanceId
    };
  }

  private loadBoInstance(option: BoAutocompleteOption) {
    return safeObserve(this.service.loadBoInstance(this.businessObjectId, option.boInstanceId));
  }

  private async completeForm(boInstance: BoInstance) {
    for (const field of this.boDetails.fields) {

      field.isReadonly = true;

      const find = boInstance.fields.find(x => x.fieldId === field.fieldId);

      if (!find) { continue; }

      this.formControl.setValue(find.fieldId, find.value);

    }

    const displayValues = this.getDisplayValues(boInstance.fields);
    const price = this.getPrice(boInstance.fields);

    this.completedBoInstance = {
      price: price,
      displayValues: displayValues,
      boInstanceId: boInstance.boInstanceId,
      canDelete: false,
      canEdit: false,
      checked: false,
      amount: undefined,
      cost: undefined,
    };
  }

  private getDisplayValues(fields: BoFieldWithValue[]) {
    const displayValues: string[] = [];
    for (const header of this.headers) {
      const find = fields.find(x => x.fieldId === header.fieldId);

      if (!find) {
        displayValues.push('');
        continue;
      }

      displayValues.push(find.displayValue);
    }

    return displayValues;
  }

  private getPrice(fields: BoFieldWithValue[]): undefined | number {

    if (this.costCalcEnabled) {

      const find = fields.find(x => x.fieldId === this.costCalcFieldId);
      if (find) { return +find.value; }

    }

    return undefined;
  }

  saveBoInstance() {

    if (!this.formControl.isValid) {
      this.formControl.markAsTouched();
      return;
    }

    if (this.completedBoInstance) {

      this.handleCompletedBoiAmount(this.completedBoInstance.boInstanceId);

      this.boInstanceAmount = 1;
      this.boInstanceAdded.emit(this.completedBoInstance);
      this.saveClicked.emit();

      this.completedBoInstance = undefined;
      return;

    }

    this.prepareBoiToSave();
    this.boInstanceCreated.emit(this.boiToSave);

    this.service.saveBoInstance(this.boiToSave).subscribe(tableRec => {

      const price = this.getPrice(this.boiToSave.fields);
      const rec: BoInstanceSelectorRecord = {
        boInstanceId: tableRec.instanceId,
        displayValues: tableRec.values.map(x => x.displayValue),
        price: price,
        canDelete: false,
        canEdit: false,
        checked: false,
        amount: this.boInstanceAmount,
        cost: price * this.boInstanceAmount,
      };

      this.saveAmount(this.boiToSave.businessId);
      this.boInstanceAdded.emit(rec);
      this.formSubs.unsubscribe();
      this.saveClicked.emit();
    }, error => {
      this.service.showAccessDeniedNotification(this.boDetails.name);
    });

  }

  private handleCompletedBoiAmount(boInstanceId: string) {
    if (!this.costCalcEnabled) { return; }

    const amount = this.boInstanceAmount;
    const price = this.completedBoInstance.price;
    this.completedBoInstance.amount = amount;
    this.completedBoInstance.cost = price * amount;

    this.saveAmount(boInstanceId);

  }

  private saveAmount(boInstanceId: string) {
    if (!this.costCalcEnabled) { return; }
    const amount = this.boInstanceAmount;
    const ownerFieldId = this.ownerFieldId;
    const amountToSave: BoInstanceAmountToSave = {
      amount, boInstanceId, ownerFieldId,
      ownerBoInstanceId: this.ownerBoInstanceId,
      ownerBoId: this.ownerBoId,
      draftId: this.boiDraftId,
    };
    this.boiAmountChanged.emit(amountToSave);
    this.service.saveBoInstanceAmount(amountToSave).subscribe();
  }

  close() {
    this.formSubs.unsubscribe();
    this.closeClicked.emit();
  }

  private prepareBoiToSave() {

    for (const fieldId of Object.keys(this.formControl.value)) {

      const value = this.formControl.value[fieldId];
      const item: BoFieldWithValue = {
        fieldId: fieldId,
        value: value,
        displayValue: undefined
      };

      this.boiToSave.fields.push(item);

    }

    const map = new Map<string, string>();

    for (const field of this.boDetails.fields) {
      map.set(field.fieldId, field.value);
    }

    for (const fieldId of Object.keys(this.formControl.value)) {
      const value = this.formControl.value[fieldId];
      map.set(fieldId, value);
    }

    for (const key of map.keys()) {
      const item: BoFieldWithValue = {
        fieldId: key,
        value: map.get(key),
        displayValue: undefined
      };
      this.boiToSave.fields.push(item);
    }


  }

}
